import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { companyInfo } from '../../utils/dataInfo';
import { formatNumberWithComma, getCategoriesNameById } from '../../utils/helpers';

// Helper function to format currency
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US').format(amount)  + " "+"sh";
};

// Manual table drawing function for PDF - Stock Taking
const drawStockTakingTable = (doc, data, startY) => {
    const pageWidth = doc.internal.pageSize.width;
    const margin = 14;
    const rowHeight = 12;

    // Column widths for stock taking
    const colWidths = [10, 60, 40, 40, 40]; // #, Stock Name, Balance/Quantity, Count Figure, Remarks

    // Check if we need a new page
    let currentY = startY;
    const neededHeight = (data.length + 1) * rowHeight;
    if (currentY + neededHeight > doc.internal.pageSize.height - 20) {
        doc.addPage();
        currentY = margin;
    }

    // Draw table header
    doc.setFillColor(255, 255, 255);
    doc.setTextColor(25, 118, 210);
    doc.setFont(undefined, 'bold');

    const headers = ['#', 'Stock Name', 'Balance/Quantity', 'Count Figure', 'Remarks'];
    let xPos = margin;

    // Draw header background
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

    // Draw header text
    headers.forEach((header, index) => {
        const cellX = xPos + 2;
        doc.text(header, cellX, currentY + 7);
        xPos += colWidths[index];
    });

    // Draw header underline
    doc.setDrawColor(25, 118, 210);
    doc.setLineWidth(0.5);
    doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);

    currentY += rowHeight;

    // Draw table rows
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);

    data.forEach((stock, rowIndex) => {
        // Check for page break
        if (currentY + rowHeight > doc.internal.pageSize.height - 20) {
            doc.addPage();
            currentY = margin;
            
            // Redraw header on new page
            doc.setFillColor(255, 255, 255);
            doc.setTextColor(25, 118, 210);
            doc.setFont(undefined, 'bold');
            
            doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
            
            xPos = margin;
            headers.forEach((header, index) => {
                doc.text(header, xPos + 2, currentY + 7);
                xPos += colWidths[index];
            });
            
            doc.setDrawColor(25, 118, 210);
            doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
            
            currentY += rowHeight;
            doc.setFont(undefined, 'normal');
            doc.setTextColor(0, 0, 0);
        }
        
        // Alternate row colors
        if (rowIndex % 2 === 0) {
            doc.setFillColor(248, 248, 248);
        } else {
            doc.setFillColor(255, 255, 255);
        }
        
        // Draw row background
        doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
        
        // Draw row data
        const rowData = [
            (rowIndex + 1).toString(),
            stock.name,
            `${stock.balance} ${stock.uom}`,
            '', // Empty for count figure
            ''  // Empty for remarks
        ];
        
        xPos = margin;
        rowData.forEach((cell, cellIndex) => {
            doc.text(cell, xPos + 2, currentY + 7);
            xPos += colWidths[cellIndex];
        });
        
        // Draw row underline
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
        
        currentY += rowHeight;
    });

    return currentY;
};

// Export stock taking to PDF
export const exportStockTakingPDF = (stocks, counterNames = [], branchNames = []) => {
    try {
        const doc = new jsPDF();
        
        // Set document properties
        doc.setProperties({
            title: 'Stock Taking Report',
            subject: 'Stock Taking Form',
            author: companyInfo.name,
            keywords: 'stock, taking, inventory, count',
            creator: companyInfo.name
        });
        
        // Title section
        doc.setFontSize(20);
        doc.setTextColor(25, 118, 210);
        doc.text('STOCK TAKING FORM', 105, 25, { align: 'center' });
        
        // Generation details
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);
        let currentY = 35;
        doc.text(`Report Generated: ${new Date().toLocaleString()}`, 14, currentY);
        currentY += 6;
        doc.text(`Total Items: ${stocks.length}`, 14, currentY);
        currentY += 6;
        
        // Instructions
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text('Instructions:', 14, currentY + 10);
        doc.text('1. Fill in the "Count Figure" column with actual counted quantity', 20, currentY + 20);
        doc.text('2. Note any discrepancies in the "Remarks" column', 20, currentY + 30);
        doc.text('3. Sign and date the completed form', 20, currentY + 40);
        
        currentY += 50;
        
        // Draw the stock taking table
        drawStockTakingTable(doc, stocks, currentY + 5);
        
        // Add signature section at the end
        const finalY = doc.internal.pageSize.height - 40;
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        
        // Signature lines
        doc.line(30, finalY, 80, finalY);
        doc.text('Counter Staff Signature', 30, finalY + 10);
        
        doc.line(110, finalY, 160, finalY);
        doc.text('Supervisor Signature', 110, finalY + 10);
        
        doc.line(190, finalY, 240, finalY);
        doc.text('Date', 190, finalY + 10);
        
        const fileName = `stock_taking_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Stock taking PDF export failed:', error);
        throw new Error('Failed to export stock taking PDF: ' + error.message);
    }
};

// Client-side Excel export for Stocks
export const exportStocksToExcel = (stocks, counterNames, branchNames, filters = {}) => {
    try {
        // Prepare data for Excel
        const excelData = stocks.map((stock, index) => ({
            'No.': index + 1,
            'ID': stock.id,
            'Name': stock.name,
            'Counter': getCategoriesNameById(stock.counter_id, counterNames),
            'UOM': stock.uom,
            'Quantity': stock.quantity,
            'Balance': stock.balance,
            'Cost Price': formatCurrency(stock.cost),
            'Selling Price': formatCurrency(stock.selling),
            'Total Cost': formatCurrency(stock.total_cost),
            'Total Selling': formatCurrency(stock.total_selling),
            'Stock Level': stock.stock_level,
            'Status': stock.status === 1 ? 'Active' : 'Inactive',
            'Stock Date': new Date(stock.stock_date).toLocaleDateString(),
            'Branch': getBranchName(stock.branch_id, branchNames),
            'Created Date': new Date(stock.created_at).toLocaleDateString(),
            'Updated Date': new Date(stock.updated_at).toLocaleDateString()
        }));

        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(excelData);

        // Auto-size columns
        const maxWidth = excelData.reduce((widths, row) => {
            Object.keys(row).forEach((key, idx) => {
                const length = String(row[key]).length;
                if (!widths[idx] || length > widths[idx]) {
                    widths[idx] = length;
                }
            });
            return widths;
        }, []);
        
        worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Stocks');

        // Generate Excel file and download
        const fileName = `stocks_export_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Stocks Excel export failed:', error);
        throw new Error('Failed to export to Excel: ' + error.message);
    }
};

// Helper functions

const getBranchName = (branchId, branchNames) => {
    if (!branchNames || !Array.isArray(branchNames)) return 'Unknown';
    const branch = branchNames.find(br => br.id === branchId);
    return branch ? `${branch.name}` : 'Unknown';
};

// Enhanced PDF export for Stocks
export const exportStocksToPDFEnhanced = (stocks, counterNames, branchNames, filters = {}) => {
    try {
        const doc = new jsPDF('landscape');

        // Set document properties
        doc.setProperties({
            title: 'Stocks Report',
            subject: 'Stocks Export',
            author: companyInfo.name,
            keywords: 'stocks, inventory, export, report',
            creator: companyInfo.name
        });
        
        // Title section
        doc.setFontSize(20);
        doc.setTextColor(25, 118, 210);
        doc.text('STOCKS REPORT', doc.internal.pageSize.width / 2, 25, { align: 'center' });
        
        // System name
        doc.setFontSize(12);
        doc.setTextColor(100, 100, 100);
        doc.text(companyInfo.name, doc.internal.pageSize.width / 2, 35, { align: 'center' });
        
        // Generation details
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);
        let currentY = 45;
        
        // Report info in two columns
        const leftColumnX = 14;
        const rightColumnX = doc.internal.pageSize.width / 2 + 20;
        
        doc.text(`Report Generated: ${new Date().toLocaleString()}`, leftColumnX, currentY);
        doc.text(`Total Stocks: ${stocks.length}`, rightColumnX, currentY);
        currentY += 8;
        
        // Calculate totals
        if (stocks.length > 0) {
            const totalBalance = stocks.reduce((sum, stock) => sum + parseInt(stock.balance || 0), 0);
            const totalValue = stocks.reduce((sum, stock) => sum + (parseInt(stock.balance || 0) * parseFloat(stock.cost || 0)), 0);
            const lowStockItems = stocks.filter(stock => parseInt(stock.balance || 0) <= 10).length;
            const outOfStockItems = stocks.filter(stock => parseInt(stock.balance || 0) <= 0).length;
            
            doc.text(`Total Balance: ${formatNumberWithComma(totalBalance)} units`, leftColumnX, currentY);
            doc.text(`Low Stock Items: ${lowStockItems}`, rightColumnX, currentY);
            currentY += 8;
            doc.text(`Total Value: ${formatNumberWithComma(totalValue)}`, leftColumnX, currentY);
            doc.text(`Out of Stock: ${outOfStockItems}`, rightColumnX, currentY);
            currentY += 8;
        }
        
        // Filters applied
        if (filters.counter_id || filters.stock_level || filters.status || filters.dateFrom || filters.dateTo) {
            doc.setFontSize(9);
            doc.setTextColor(60, 60, 60);
            doc.text('Applied Filters:', leftColumnX, currentY);
            currentY += 6;
            
            let filterLines = [];
            if (filters.counter_id) {
                const counterName = getCategoriesNameById(filters.counter_id, counterNames);
                filterLines.push(`• Counter: ${counterName}`);
            }
            if (filters.stock_level) filterLines.push(`• Stock Level: ${filters.stock_level}`);
            if (filters.status) filterLines.push(`• Status: ${filters.status === '1' ? 'Active' : 'Inactive'}`);
            if (filters.dateFrom) filterLines.push(`• From date: ${filters.dateFrom}`);
            if (filters.dateTo) filterLines.push(`• To date: ${filters.dateTo}`);
            
            // Display filters in two columns if many
            const midPoint = Math.ceil(filterLines.length / 2);
            const leftFilters = filterLines.slice(0, midPoint);
            const rightFilters = filterLines.slice(midPoint);
            
            const maxLines = Math.max(leftFilters.length, rightFilters.length);
            
            for (let i = 0; i < maxLines; i++) {
                if (leftFilters[i]) {
                    doc.text(leftFilters[i], leftColumnX + 5, currentY);
                }
                if (rightFilters[i]) {
                    doc.text(rightFilters[i], rightColumnX, currentY);
                }
                currentY += 6;
            }
            currentY += 6;
        }
        
        // Prepare table data with selected columns
        const tableData = stocks.map((stock, index) => {
            const stockLevel = getStockLevel(stock.balance);
            const statusText = stock.status === 1 ? 'Active' : 'Inactive';
            
            return {
                no: (index + 1).toString(),
                name: stock.name,
                counter: getCategoriesNameById(stock.counter_id, counterNames),
                uom: stock.uom || 'N/A',
                balance: formatNumberWithComma(stock.balance || 0),
                cost: formatNumberWithComma(stock.cost || 0),
                selling: formatNumberWithComma(stock.selling || 0),
                level: stockLevel,
                status: statusText
            };
        });
        
        // Draw the table
        currentY = drawStocksTableEnhanced(doc, tableData, currentY + 10);
        
        // Add footer with page number
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            doc.text(
                `Page ${i} of ${pageCount}`,
                doc.internal.pageSize.width / 2,
                doc.internal.pageSize.height - 10,
                { align: 'center' }
            );
            doc.text(
                `Generated on: ${new Date().toLocaleDateString()}`,
                doc.internal.pageSize.width - 14,
                doc.internal.pageSize.height - 10,
                { align: 'right' }
            );
        }
        
        const fileName = `stocks_report_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Stocks PDF export failed:', error);
        throw new Error('Failed to export to PDF: ' + error.message);
    }
};

// Helper function to draw stocks table without autoTable
const drawStocksTableEnhanced = (doc, tableData, startY) => {
    // Table configuration
    const marginLeft = 14;
    const marginRight = 14;
    const pageWidth = doc.internal.pageSize.width;
    const tableWidth = pageWidth - marginLeft - marginRight;
    
    // Column widths for landscape mode
    const colWidths = {
        no: 10,
        name: 40,
        counter: 35,
        uom: 20,
        balance: 25,
        cost: 25,
        selling: 25,
        level: 25,
        status: 20
    };
    
    // Calculate total width and adjust if needed
    const totalWidth = Object.values(colWidths).reduce((sum, width) => sum + width, 0);
    if (totalWidth > tableWidth) {
        const scaleFactor = tableWidth / totalWidth;
        Object.keys(colWidths).forEach(key => {
            colWidths[key] = Math.floor(colWidths[key] * scaleFactor);
        });
    }
    
    // Draw table header
    let currentX = marginLeft;
    let currentY = startY;
    
    // Header background
    doc.setFillColor(41, 128, 185); // Blue color
    doc.rect(currentX, currentY, tableWidth, 10, 'F');
    
    // Header text
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    
    // Column headers
    const headers = ['#', 'Item Name', 'Counter', 'UOM', 'Balance', 'Cost', 'Selling', 'Stock Level', 'Status'];
    
    currentX = marginLeft + 2;
    doc.text('#', currentX, currentY + 7);
    currentX += colWidths.no;
    
    doc.text('Item Name', currentX + 2, currentY + 7);
    currentX += colWidths.name;
    
    doc.text('Counter', currentX + 2, currentY + 7);
    currentX += colWidths.counter;
    
    doc.text('UOM', currentX + 2, currentY + 7);
    currentX += colWidths.uom;
    
    doc.text('Balance', currentX + 2, currentY + 7);
    currentX += colWidths.balance;
    
    doc.text('Cost', currentX + 2, currentY + 7);
    currentX += colWidths.cost;
    
    doc.text('Selling', currentX + 2, currentY + 7);
    currentX += colWidths.selling;
    
    doc.text('Stock Level', currentX + 2, currentY + 7);
    currentX += colWidths.level;
    
    doc.text('Status', currentX + 2, currentY + 7);
    
    // Draw rows
    currentY += 12; // Move down for first row
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    
    let rowIndex = 0;
    const rowHeight = 8;
    const pageHeight = doc.internal.pageSize.height;
    
    for (const row of tableData) {
        // Check if we need a new page
        if (currentY > pageHeight - 30) {
            doc.addPage('landscape');
            currentY = 20;
            
            // Redraw header on new page
            doc.setFillColor(41, 128, 185);
            doc.rect(marginLeft, currentY, tableWidth, 10, 'F');
            doc.setFontSize(10);
            doc.setTextColor(255, 255, 255);
            doc.setFont(undefined, 'bold');
            
            currentX = marginLeft + 2;
            doc.text('#', currentX, currentY + 7);
            currentX += colWidths.no;
            
            doc.text('Item Name', currentX + 2, currentY + 7);
            currentX += colWidths.name;
            
            doc.text('Counter', currentX + 2, currentY + 7);
            currentX += colWidths.counter;
            
            doc.text('UOM', currentX + 2, currentY + 7);
            currentX += colWidths.uom;
            
            doc.text('Balance', currentX + 2, currentY + 7);
            currentX += colWidths.balance;
            
            doc.text('Cost', currentX + 2, currentY + 7);
            currentX += colWidths.cost;
            
            doc.text('Selling', currentX + 2, currentY + 7);
            currentX += colWidths.selling;
            
            doc.text('Stock Level', currentX + 2, currentY + 7);
            currentX += colWidths.level;
            
            doc.text('Status', currentX + 2, currentY + 7);
            
            currentY += 12;
            doc.setFontSize(9);
            doc.setFont(undefined, 'normal');
        }
        
        // Alternate row background
        if (rowIndex % 2 === 0) {
            doc.setFillColor(245, 245, 245);
        } else {
            doc.setFillColor(255, 255, 255);
        }
        doc.rect(marginLeft, currentY - 2, tableWidth, rowHeight + 2, 'F');
        
        // Draw cell borders
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.1);
        
        currentX = marginLeft;
        for (const width of Object.values(colWidths)) {
            doc.line(currentX, currentY - 2, currentX, currentY + rowHeight);
            currentX += width;
        }
        doc.line(currentX, currentY - 2, currentX, currentY + rowHeight); // Last vertical line
        
        // Draw horizontal line at bottom of row
        doc.line(marginLeft, currentY + rowHeight, marginLeft + tableWidth, currentY + rowHeight);
        
        // Draw row data
        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);
        
        currentX = marginLeft + 2;
        
        // Row number
        doc.text(row.no, currentX, currentY + 6);
        currentX += colWidths.no;
        
        // Item Name (truncate if too long)
        const itemName = row.name.length > 25 ? row.name.substring(0, 25) + '...' : row.name;
        doc.text(itemName, currentX + 2, currentY + 6);
        currentX += colWidths.name;
        
        // Counter (truncate if too long)
        const counterName = row.counter.length > 20 ? row.counter.substring(0, 20) + '...' : row.counter;
        doc.text(counterName, currentX + 2, currentY + 6);
        currentX += colWidths.counter;
        
        // UOM
        doc.text(row.uom, currentX + 2, currentY + 6);
        currentX += colWidths.uom;
        
        // Balance
        doc.text(row.balance, currentX + 2, currentY + 6);
        currentX += colWidths.balance;
        
        // Cost
        doc.text(row.cost, currentX + 2, currentY + 6);
        currentX += colWidths.cost;
        
        // Selling
        doc.text(row.selling, currentX + 2, currentY + 6);
        currentX += colWidths.selling;
        
        // Stock Level with color coding
        const levelColor = getStockLevelColor(row.level);
        doc.setTextColor(levelColor.r, levelColor.g, levelColor.b);
        doc.text(row.level, currentX + 2, currentY + 6);
        doc.setTextColor(0, 0, 0);
        currentX += colWidths.level;
        
        // Status with color coding
        const statusColor = row.status === 'Active' ? {r: 0, g: 150, b: 0} : {r: 255, g: 0, b: 0};
        doc.setTextColor(statusColor.r, statusColor.g, statusColor.b);
        doc.text(row.status, currentX + 2, currentY + 6);
        doc.setTextColor(0, 0, 0);
        
        currentY += rowHeight + 2;
        rowIndex++;
    }
    
    // Draw top border of table
    doc.setDrawColor(41, 128, 185);
    doc.setLineWidth(0.5);
    doc.line(marginLeft, startY - 2, marginLeft + tableWidth, startY - 2);
    
    return currentY + 10;
};

// Helper function to get stock level
const getStockLevel = (balance) => {
    const balanceNum = parseInt(balance || 0);
    if (balanceNum <= 0) return 'Out of Stock';
    if (balanceNum <= 10) return 'Low';
    if (balanceNum <= 50) return 'Normal';
    return 'High';
};

// Helper function to get stock level color
const getStockLevelColor = (level) => {
    switch(level) {
        case 'Out of Stock': return {r: 255, g: 0, b: 0}; // Red
        case 'Low': return {r: 255, g: 165, b: 0}; // Orange
        case 'Normal': return {r: 0, g: 150, b: 0}; // Green
        case 'High': return {r: 0, g: 0, b: 255}; // Blue
        default: return {r: 0, g: 0, b: 0}; // Black
    }
};

// Simple CSV export for Stocks
export const exportStocksToCSV = (stocks, counterNames, branchNames) => {
    try {
        const headers = ['ID', 'Name', 'Counter', 'UOM', 'Quantity', 'Balance', 'Cost', 'Selling', 'Stock Level', 'Status', 'Stock Date', 'Created Date'];
        const csvData = stocks.map(stock => [
            stock.id,
            `"${stock.name.replace(/"/g, '""')}"`,
            `"${getCategoriesNameById(stock.counter_id, counterNames).replace(/"/g, '""')}"`,
            `"${stock.uom.replace(/"/g, '""')}"`,
            stock.quantity,
            stock.balance,
            stock.cost,
            stock.selling,
            `"${stock.stock_level.replace(/"/g, '""')}"`,
            stock.status === 1 ? 'Active' : 'Inactive',
            `"${new Date(stock.stock_date).toLocaleDateString()}"`,
            `"${new Date(stock.created_at).toLocaleDateString()}"`
        ]);

        const csvContent = [
            headers.join(','),
            ...csvData.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `stocks_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        return { success: true };
    } catch (error) {
        console.error('Stocks CSV export failed:', error);
        throw new Error('Failed to export to CSV: ' + error.message);
    }
};



export const exportStockHistoryPDF = (stock, history, userNames) => {
    try {
        const doc = new jsPDF('landscape', 'mm', 'a4');
        
        // Set document properties
        doc.setProperties({
            title: 'Stock History Report - ' + stock.name,
            subject: 'Stock Adjustment History',
            author: companyInfo.name,
            keywords: 'stock, history, adjustment, report',
            creator: companyInfo.name
        });
        
        // Calculate summary
        const totalAdjustments = history.length;
        const totalIncrease = history.filter(h => h.quantity_change > 0)
            .reduce((sum, h) => sum + h.quantity_change, 0);
        const totalDecrease = Math.abs(history.filter(h => h.quantity_change < 0)
            .reduce((sum, h) => sum + h.quantity_change, 0));
        
        // Title section
        doc.setFontSize(20);
        doc.setTextColor(25, 118, 210);
        doc.text('STOCK ADJUSTMENT HISTORY REPORT', 148, 20, { align: 'center' });
        
        doc.setFontSize(12);
        doc.setTextColor(80, 80, 80);
        doc.text(`For: ${stock.name}`, 148, 30, { align: 'center' });
        
        // Generation details
        doc.setFontSize(10);
        doc.text(`Report Generated: ${new Date().toLocaleString()}`, 14, 40);
        doc.text(`Total Adjustments: ${totalAdjustments}`, 14, 47);
        
        // Stock Information Section
        doc.setFontSize(11);
        doc.setTextColor(25, 118, 210);
        doc.text('STOCK INFORMATION', 14, 60);
        
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        
        let yPos = 68;
        const infoLines = [
            `Item Name: ${stock.name}`,
            `Current Balance: ${formatNumberWithComma(stock.balance)} ${stock.uom}`,
            `Counter: ${getCategoriesNameById(stock.counter_id, userNames)}`,
            `Unit of Measure: ${stock.uom}`,
            `Cost Price: KES ${formatNumberWithComma(stock.cost, 2)}`,
            `Selling Price: KES ${formatNumberWithComma(stock.selling, 2)}`,
            `Stock Level: ${stock.stock_level}`,
            `Status: ${stock.status === 1 ? 'Active' : 'Inactive'}`
        ];
        
        infoLines.forEach(line => {
            doc.text(line, 14, yPos);
            yPos += 6;
        });
        
        // Summary Section
        doc.setFontSize(11);
        doc.setTextColor(25, 118, 210);
        doc.text('SUMMARY', 200, 60);
        
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        
        yPos = 68;
        doc.text(`Total Adjustments: ${totalAdjustments}`, 200, yPos);
        yPos += 6;
        doc.setTextColor(39, 174, 96);
        doc.text(`Total Increase: +${formatNumberWithComma(totalIncrease)} ${stock.uom}`, 200, yPos);
        yPos += 6;
        doc.setTextColor(231, 76, 60);
        doc.text(`Total Decrease: -${formatNumberWithComma(totalDecrease)} ${stock.uom}`, 200, yPos);
        yPos += 6;
        doc.setTextColor(0, 0, 0);
        doc.text(`Current Balance: ${formatNumberWithComma(stock.balance)} ${stock.uom}`, 200, yPos);
        
        // History Table Header
        yPos = 95;
        doc.setFontSize(11);
        doc.setTextColor(25, 118, 210);
        doc.text('ADJUSTMENT HISTORY', 14, yPos);
        yPos += 10;
        
        // Draw table
        yPos = drawHistoryTable(doc, stock, history, userNames, yPos);
        
        // Add footer
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            doc.text(`Page ${i} of ${pageCount}`, 148, 205, { align: 'center' });
            doc.text(`Generated by ${companyInfo.name}`, 148, 210, { align: 'center' });
        }
        
        const fileName = `stock_history_${stock.name}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
        
        return { success: true, fileName };
    } catch (error) {
        console.error('Stock history PDF export failed:', error);
        throw new Error('Failed to export stock history PDF: ' + error.message);
    }
};

// Helper function to draw history table
const drawHistoryTable = (doc, stock, history, userNames, startY) => {
    const pageWidth = doc.internal.pageSize.width;
    const margin = 14;
    const rowHeight = 12;
    
    // Column widths for landscape
    const colWidths = [15, 30, 35, 40, 25, 25, 25, 40];
    const headers = ['#', 'Date', 'Action Type', 'User', 'Old Qty', 'Change', 'New Qty', 'Reason'];
    
    let currentY = startY;
    const maxRowsPerPage = Math.floor((doc.internal.pageSize.height - currentY - 30) / rowHeight);
    let processedRows = 0;
    let page = 1;
    
    while (processedRows < history.length) {
        if (page > 1) {
            doc.addPage('landscape');
            currentY = margin;
            
            // Draw header on new page
            doc.setFontSize(11);
            doc.setTextColor(25, 118, 210);
            doc.text('ADJUSTMENT HISTORY (Continued)', 14, currentY);
            currentY += 10;
        }
        
        // Draw table header
        doc.setFillColor(52, 152, 219);
        doc.setTextColor(255, 255, 255);
        doc.setFont(undefined, 'bold');
        doc.setFontSize(9);
        
        let xPos = margin;
        doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
        
        headers.forEach((header, index) => {
            doc.text(header, xPos + 2, currentY + 8);
            xPos += colWidths[index];
        });
        
        doc.setDrawColor(52, 152, 219);
        doc.setLineWidth(0.5);
        doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
        
        currentY += rowHeight;
        
        // Draw table rows
        doc.setFont(undefined, 'normal');
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(8);
        
        const rowsToProcess = Math.min(maxRowsPerPage, history.length - processedRows);
        
        for (let i = 0; i < rowsToProcess; i++) {
            const historyItem = history[processedRows + i];
            const rowIndex = processedRows + i;
            
            // Alternate row colors
            if (rowIndex % 2 === 0) {
                doc.setFillColor(248, 248, 248);
            } else {
                doc.setFillColor(255, 255, 255);
            }
            
            // Draw row background
            doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
            
            // Row data
            const rowData = [
                (rowIndex + 1).toString(),
                new Date(historyItem.created_at).toLocaleDateString(),
                historyItem.action_type.charAt(0).toUpperCase() + historyItem.action_type.slice(1),
                getCategoriesNameById(historyItem.user_id, userNames),
                formatNumberWithComma(historyItem.old_quantity),
                (historyItem.quantity_change >= 0 ? '+' : '') + formatNumberWithComma(historyItem.quantity_change),
                formatNumberWithComma(historyItem.new_quantity),
                historyItem.reason || 'N/A'
            ];
            
            xPos = margin;
            rowData.forEach((cell, cellIndex) => {
                // Set color for change column
                if (cellIndex === 5) {
                    doc.setTextColor(historyItem.quantity_change >= 0 ? 39 : 231);
                } else {
                    doc.setTextColor(0, 0, 0);
                }
                
                // Truncate long text
                let displayText = cell;
                if (cellIndex === 7 && doc.getTextWidth(cell) > colWidths[cellIndex]) {
                    displayText = cell.substring(0, 20) + '...';
                }
                
                doc.text(displayText, xPos + 2, currentY + 8);
                xPos += colWidths[cellIndex];
            });
            
            // Draw row separator
            doc.setDrawColor(200, 200, 200);
            doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
            
            currentY += rowHeight;
        }
        
        processedRows += rowsToProcess;
        page++;
        
        if (processedRows < history.length) {
            currentY = margin;
        }
    }
    
    return currentY;
};

